import { useEffect, useMemo, useRef, useState } from "react";
import { BookOpen, ChevronDown, Lightbulb, Loader2, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAITutor, type QuizExplanationData } from "@/hooks/useAITutor";

interface Props {
  lessonId: string;
  lessonName: string;
  courseName: string;
  questionId?: string;
  questionText: string;
  answerOptions: { key: string; text: string }[];
  selectedAnswer: string;
  correctAnswer: string;
  staticExplanation?: string | null;
  hintUsageCount?: number;
  responseTimeMs?: number | null;
  onTeachTerm?: (term: string) => void;
}

// Module-level caches survive remounts (e.g. resize/scroll-driven re-renders)
const explanationCache = new Map<string, QuizExplanationData>();
const inFlightRequests = new Map<string, Promise<QuizExplanationData | null>>();

export default function AIQuizExplanationPanel({
  lessonId,
  lessonName,
  courseName,
  questionId,
  questionText,
  answerOptions,
  selectedAnswer,
  correctAnswer,
  staticExplanation,
  hintUsageCount = 0,
  responseTimeMs,
  onTeachTerm,
}: Props) {
  const { getQuizExplanation } = useAITutor();

  const selectedAnswerText = useMemo(
    () => answerOptions.find((option) => option.key === selectedAnswer)?.text || selectedAnswer,
    [answerOptions, selectedAnswer]
  );

  const correctAnswerText = useMemo(
    () => answerOptions.find((option) => option.key === correctAnswer)?.text || correctAnswer,
    [answerOptions, correctAnswer]
  );

  const cacheKey = useMemo(
    () => `${lessonId}::${questionId ?? "noq"}::${selectedAnswer}::${correctAnswer}`,
    [lessonId, questionId, selectedAnswer, correctAnswer]
  );

  const [data, setData] = useState<QuizExplanationData | null>(() => explanationCache.get(cacheKey) ?? null);
  const [fetching, setFetching] = useState<boolean>(() => !explanationCache.has(cacheKey));
  const [showMore, setShowMore] = useState(false);
  const [showExample, setShowExample] = useState(false);
  const lastKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (lastKeyRef.current === cacheKey) return;
    lastKeyRef.current = cacheKey;

    // Reset disclosure toggles for a new question, but never blank a cached answer.
    setShowMore(false);
    setShowExample(false);

    const cached = explanationCache.get(cacheKey);
    if (cached) {
      setData(cached);
      setFetching(false);
      return;
    }

    setData(null);
    setFetching(true);

    let cancelled = false;

    const existing = inFlightRequests.get(cacheKey);
    const promise =
      existing ??
      getQuizExplanation({
        lessonId,
        lessonName,
        courseName,
        questionId,
        questionText,
        answerOptions,
        selectedAnswer,
        correctAnswer,
        selectedAnswerText,
        correctAnswerText,
        wasCorrect: selectedAnswer === correctAnswer,
        staticExplanation,
        responseTimeMs: responseTimeMs ?? undefined,
        hintUsageCount,
        responseMode: "short",
      }).then((result) => {
        if (result.data) explanationCache.set(cacheKey, result.data);
        return result.data ?? null;
      }).finally(() => {
        inFlightRequests.delete(cacheKey);
      });

    if (!existing) inFlightRequests.set(cacheKey, promise);

    promise.then((value) => {
      if (cancelled) return;
      if (value) setData(value);
      setFetching(false);
    });

    return () => {
      cancelled = true;
    };
    // Intentionally only depend on cacheKey — other inputs are derived from the same identifiers.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cacheKey]);

  if (fetching && !data) {
    return (
      <div className="flex items-center justify-center gap-2 rounded-2xl border border-border/60 bg-card/95 backdrop-blur-xl px-4 py-4 text-xs text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin text-primary" />
        HeirWise AI is explaining this answer…
      </div>
    );
  }

  const fallbackExplanation = staticExplanation || "Review the key idea behind this question, then compare how each answer choice changes the outcome.";
  const vocabularyTerm = data?.vocabulary_term?.trim();

  return (
    <div className="space-y-3 text-left">
      <div className="rounded-2xl border border-border/60 bg-card/95 backdrop-blur-xl p-4 shadow-lg">
        <div className="mb-2 flex items-center gap-2 text-primary">
          <Sparkles className="h-4 w-4" />
          <span className="text-xs md:text-sm font-semibold uppercase tracking-[0.18em]">AI Explanation</span>
        </div>

        {data ? (
          <div className="space-y-3">
            <div>
              <p className="text-sm md:text-base font-semibold text-foreground">{data.feedback_headline}</p>
              <p className="mt-1 text-sm md:text-base leading-relaxed text-muted-foreground">{data.explanation}</p>
            </div>

            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => setShowMore((value) => !value)}
                className="gap-1.5 text-xs"
              >
                <ChevronDown className={`h-3.5 w-3.5 transition-transform ${showMore ? "rotate-180" : ""}`} />
                Explain more
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => setShowExample((value) => !value)}
                className="gap-1.5 text-xs"
              >
                <BookOpen className="h-3.5 w-3.5" />
                Give me another example
              </Button>
              {vocabularyTerm && onTeachTerm && (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => onTeachTerm(vocabularyTerm)}
                  className="gap-1.5 text-xs"
                >
                  <Lightbulb className="h-3.5 w-3.5" />
                  Teach me this term
                </Button>
              )}
            </div>

            {showMore && (
              <div className="space-y-3 rounded-2xl border border-border/50 bg-card/90 p-3 md:p-4">
                <div>
                  <p className="text-xs md:text-sm font-semibold uppercase tracking-[0.18em] text-primary">Concept tested</p>
                  <p className="mt-1 text-xs md:text-sm leading-relaxed text-muted-foreground">{data.concept_tested}</p>
                </div>
                <div>
                  <p className="text-xs md:text-sm font-semibold uppercase tracking-[0.18em] text-primary">Why the wrong answer felt tempting</p>
                  <p className="mt-1 text-xs md:text-sm leading-relaxed text-muted-foreground">{data.trap_answer_reason}</p>
                </div>
              </div>
            )}

            {showExample && (
              <div className="rounded-2xl border border-border/50 bg-card/90 p-3 md:p-4">
                <p className="text-xs md:text-sm font-semibold uppercase tracking-[0.18em] text-primary">Example</p>
                <p className="mt-1 text-xs md:text-sm leading-relaxed text-muted-foreground">{data.example}</p>
              </div>
            )}

            <p className="text-xs md:text-sm leading-relaxed text-muted-foreground">{data.encouragement}</p>
          </div>
        ) : (
          <p className="text-sm leading-relaxed text-muted-foreground">{fallbackExplanation}</p>
        )}
      </div>
    </div>
  );
}
